import { AuthenticationRespDtoDTO, FacilityDTO, MyProfileRespDtoDTO, ResponseAuthenticationRespDtoDTO, ResponseErrorDTO } from '@hem/portal-query'
import AxiosInstance, { AxiosError, AxiosResponse } from 'axios'
import axios from 'axios'

import APIS from './constants'

export const exchangeToken = (
  accessCode: string,
  redirectUrl: string,
  baseURL: string
) => {
  const api = AxiosInstance.create()
  return api
    .post(`${baseURL}${APIS.accounts.token}`, {
      code: accessCode,
      redirectUrl,
    })
    .then((data) => data)
}

export const refreshToken = (
  refreshToken: string,
  baseURL: string,
) => {
  const api = AxiosInstance.create()
  return api
    .post(`${baseURL}${APIS.accounts.refreshToken}`, {
      refreshToken: refreshToken,
    })
    .then((data) => data)
}

export class RequestOtpException extends Error {
  errorMessage: 'REQUEST_OTP_COOLDOWN' | 'REQUEST_OTP_RATE_LIMIT' | 'REQUEST_OTP_FAILED'

  constructor(errorMessage: 'REQUEST_OTP_COOLDOWN' | 'REQUEST_OTP_RATE_LIMIT' | 'REQUEST_OTP_FAILED', message: string) {
    super(message)
    this.name = 'RequestOtpException'
    this.errorMessage = errorMessage
  }
}

export const requestOtp = async (email: string, baseURL: string): Promise<AuthenticationRespDtoDTO | RequestOtpException> => {
  const api = AxiosInstance.create()
  try {
    const response = await api.post(`${baseURL}${APIS.accounts.requestOtp}`, { email }) as AxiosResponse<AuthenticationRespDtoDTO>
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ResponseErrorDTO>
      const statusCode = axiosError.response?.status

      if (statusCode === 429) {
        const errorCode = axiosError.response?.data.errors ? axiosError.response.data.errors[0].code : null
        if (errorCode === 'ACC015') {
          throw new RequestOtpException('REQUEST_OTP_COOLDOWN', 'We have sent an OTP to your email address. Please check your email or try again after 1 minute.')
        } else if (errorCode === 'ACC013') {
          throw new RequestOtpException('REQUEST_OTP_RATE_LIMIT', 'You have reached the maximum limit for requesting a one-time passcode. Please wait for 5 minutes before requesting a new passcode.')
        }
      }
    }

    throw new RequestOtpException('REQUEST_OTP_FAILED', 'Failed to proceed your request, please check your Internet connection and try again.')
  }
}

export class VerifyOtpException extends Error {
  errorMessage: 'VERIFY_OTP_FAILED' | 'VERIFY_OTP_RATE_LIMIT' | 'VERIFY_OTP_ACCOUNT_LOCKED' | 'ACCOUNT_MAY_NOT_BE_LINKED'
  constructor(errorMessage: 'VERIFY_OTP_FAILED' | 'VERIFY_OTP_RATE_LIMIT' | 'VERIFY_OTP_ACCOUNT_LOCKED' | 'ACCOUNT_MAY_NOT_BE_LINKED', message: string) {
    super(message)
    this.name = 'VerifyOtpException'
    this.errorMessage = errorMessage
  }
}

export const verifyOtp = async (
  email: string,
  otpCode: string,
  baseURL: string
): Promise<ResponseAuthenticationRespDtoDTO> => {
  const api = AxiosInstance.create()

  try {
    const response = await api.post(`${baseURL}${APIS.accounts.verifyOtp}`, {
      email,
      otpCode,
    }) as AxiosResponse<ResponseAuthenticationRespDtoDTO>
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ResponseErrorDTO>
      const apiError = axiosError.response?.data.errors?.[0]
      const errorCode = apiError?.code

      switch (errorCode) {
        case 'ACC017':
          throw new VerifyOtpException('ACCOUNT_MAY_NOT_BE_LINKED', 'Your Clinic@Home Web Portal account may be linked to a different email. Please try an alternate email address or contact for support.')
        case 'ACC006': {
          const attemptsLeft = apiError?.value || '0'
          throw new VerifyOtpException('VERIFY_OTP_RATE_LIMIT', `The passcode is invalid or has expired. Please try again (${attemptsLeft} times left)`)
        }
        case 'ACC012':
          throw new VerifyOtpException('VERIFY_OTP_FAILED', 'The passcode is invalid or has expired. Please try again.')
        case 'ACC014':
        case 'ACC018':
          throw new VerifyOtpException('VERIFY_OTP_ACCOUNT_LOCKED', 'Sorry, your account temporarily blocked. Please contact Admin to activate it again.')
      }
    }

    throw new VerifyOtpException('VERIFY_OTP_FAILED', 'Failed to proceed your request, please check your Internet connection and try again.')
  }
}

export const getMyProfile = async (baseURL: string, token: string): Promise<MyProfileRespDtoDTO> => {
  const api = AxiosInstance.create()
  api.defaults.headers.common.Authorization = `Bearer ${token}`

  const userRes = await api.get(`${baseURL}/accounts/my-profile`)
  return userRes.data?.data
}

export const getCurrentFacility = async (baseURL: string, token: string): Promise<FacilityDTO[]> => {
  const api = AxiosInstance.create()
  api.defaults.headers.common.Authorization = `Bearer ${token}`

  const facilityRes = await api.get(`${baseURL}/facilities`)
  return facilityRes.data?.data
}
